import React, { useState } from 'react';
import { Card, Upload, Button, message, Row, Col, Table, Tag } from 'antd';
import { UploadOutlined, SyncOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';

interface UploadLog {
    id: number;
    shop_name: string;
    file_name: string;
    file_type: string;
    status: 'pending' | 'success' | 'failed';
    status_display: string;
    records_new: number;
    records_updated: number;
    created_at: string;
    error_message: string;
}

interface Props {
    shopId: number | null;
    history: UploadLog[];
    historyLoading: boolean;
    onRefreshHistory: () => void;
}

const DataUploadTab: React.FC<Props> = ({ shopId, history, historyLoading, onRefreshHistory }) => {
    const [ordersUploadCount, setOrdersUploadCount] = useState(0);
    const [businessUploadCount, setBusinessUploadCount] = useState(0);
    const [currentOrdersFile, setCurrentOrdersFile] = useState<string>('');
    const [currentBusinessFile, setCurrentBusinessFile] = useState<string>('');
    const [uploadQueue, setUploadQueue] = useState<{ file: File, type: 'orders' | 'business' }[]>([]);
    const [processing, setProcessing] = useState(false);

    React.useEffect(() => {
        const processQueue = async () => {
            if (processing || uploadQueue.length === 0) return;

            setProcessing(true);
            const { file, type } = uploadQueue[0];
            const isOrders = type === 'orders';
            const setCounter = isOrders ? setOrdersUploadCount : setBusinessUploadCount;
            const setCurrentFile = isOrders ? setCurrentOrdersFile : setCurrentBusinessFile;

            setCurrentFile(file.name);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('shop_id', shopId!.toString());

            setCounter(prev => prev + 1);

            try {
                const endpoint = isOrders ? '/data-analysis/upload-orders/' : '/data-analysis/upload-business-report/';
                const res = await api.post(endpoint, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                message.success(`上传成功: ${file.name} (新增 ${res.data.created}, 更新 ${res.data.updated})`);
                await onRefreshHistory();
            } catch (error: any) {
                console.error(error);
                const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
                message.error(`上传失败: ${file.name} - ${errorMsg}`);
                await onRefreshHistory();
            } finally {
                setCounter(prev => Math.max(0, prev - 1));
                setUploadQueue(prev => prev.slice(1));
                setCurrentFile('');
                setProcessing(false);
            }
        };

        processQueue();
    }, [uploadQueue, processing, shopId, onRefreshHistory]);

    const handleUpload = (file: File, type: 'orders' | 'business') => {
        if (!shopId) {
            message.error('请先选择店铺');
            return false;
        }
        setUploadQueue(prev => [...prev, { file, type }]);
        return false;
    };

    const historyColumns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '店铺',
            dataIndex: 'shop_name',
            key: 'shop_name',
            width: 150,
        },
        {
            title: '文件名',
            dataIndex: 'file_name',
            key: 'file_name',
        },
        {
            title: '类型',
            dataIndex: 'file_type',
            key: 'file_type',
            width: 120,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string, record: UploadLog) => {
                const color = status === 'success' ? 'green' : status === 'failed' ? 'red' : 'blue';
                return <Tag color={color}>{record.status_display}</Tag>;
            }
        },
        {
            title: '结果',
            key: 'result',
            width: 200,
            render: (_: any, record: UploadLog) => {
                if (record.status === 'success') {
                    return <span>新增: {record.records_new} | 更新: {record.records_updated}</span>;
                }
                return <span style={{ color: 'red' }}>{record.error_message}</span>;
            }
        },
    ];

    return (
        <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="订单报告 (sy-11.txt)">
                        <p>上传 "All Orders" 报告以同步订单</p>
                        <Upload
                            beforeUpload={(f) => handleUpload(f, 'orders')}
                            showUploadList={false}
                            multiple
                            accept=".txt"
                        >
                            <Button
                                icon={<UploadOutlined />}
                                loading={ordersUploadCount > 0}
                                disabled={!shopId}
                            >
                                {ordersUploadCount > 0 ? `正在处理: ${currentOrdersFile}` : '点击上传 / 批量拖拽'}
                            </Button>
                        </Upload>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="业务报告 (流量)">
                        <p>上传 "按子商品" 的业务报告 (CSV)</p>
                        <Upload
                            beforeUpload={(f) => handleUpload(f, 'business')}
                            showUploadList={false}
                            multiple
                            accept=".csv"
                        >
                            <Button
                                icon={<UploadOutlined />}
                                loading={businessUploadCount > 0}
                                disabled={!shopId}
                            >
                                {businessUploadCount > 0 ? `正在处理: ${currentBusinessFile}` : '点击上传 / 批量拖拽'}
                            </Button>
                        </Upload>
                    </Card>
                </Col>
            </Row>

            <Card
                title="上传历史"
                extra={<Button icon={<SyncOutlined />} onClick={onRefreshHistory} loading={historyLoading}>刷新</Button>}
            >
                <Table
                    columns={historyColumns}
                    dataSource={history}
                    rowKey="id"
                    loading={historyLoading}
                    pagination={{ pageSize: 10 }}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default DataUploadTab;
